/**
 * TOAST PROVIDER — Sistema di notifiche globali.
 *
 * Backend: `react-toastify`. Il wrapper mantiene l'API pubblica storica
 * (useToast + ToastMessage shape {severity, message}) cosi' i 25+
 * call-site esistenti non vengono toccati. Il rendering e' delegato
 * al ToastContainer della libreria: stacking di toast multipli,
 * animazioni, progress bar, theming — risolve anche il bug latente
 * del vecchio MUI Snackbar che sovrascriveva il toast corrente quando
 * ne arrivava uno nuovo ravvicinato.
 *
 * Esempio (invariato):
 *   const toast = useToast();
 *   toast({ severity: 'success', message: 'Salvato' });
 */

import { createContext, useCallback, type ReactNode } from 'react';
import { ToastContainer, toast as toastify, type ToastOptions } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import type { AlertColor } from '@mui/material/Alert';

export interface ToastMessage {
  severity: AlertColor;
  message: string;
}

interface ToastContextValue {
  push: (toast: ToastMessage) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ToastContext = createContext<ToastContextValue | null>(null);

interface ToastProviderProps {
  children: ReactNode;
  autoHideDuration?: number;
}

// Mappa severity MUI-compatibile -> metodo toastify. Alias 'warning' ->
// `toastify.warn` perche' nella v11 esiste solo l'alias breve come helper.
const SEVERITY_TO_TOASTIFY: Record<AlertColor, (msg: string, opts?: ToastOptions) => void> = {
  success: toastify.success,
  error: toastify.error,
  info: toastify.info,
  warning: toastify.warn,
};

export function ToastProvider({ children, autoHideDuration = 4000 }: ToastProviderProps) {
  const push = useCallback((t: ToastMessage) => {
    const fn = SEVERITY_TO_TOASTIFY[t.severity] ?? toastify.info;
    fn(t.message, { autoClose: autoHideDuration });
  }, [autoHideDuration]);

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      {/* Container unico globale. `theme="colored"` applica uno sfondo
          colorato per severity (verde/rosso/blu/arancio) coerente con
          la vecchia variante MUI "filled". Lo stacking e' in basso a
          destra come prima. */}
      <ToastContainer
        position="bottom-left"
        autoClose={autoHideDuration}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </ToastContext.Provider>
  );
}
